import { Aide } from '@/components/ui/aide';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { api } from '../lib/api';
import { messageErreur } from '../lib/erreurs';
import { formatDate, formatDateHeure, formatMontant } from '../lib/formats';
import { libelle, nomLisible, tableAvecRepli, texteSur } from '../lib/libelles';
import { SIGNES_TIERS, signeCaisse } from '../lib/argentApi';
import { usePagination } from '../hooks/usePagination';
import { BarrePagination } from './ui/pagination-bar';
import { Button } from './ui/button';
import { LoadingState } from './ui/loading-state';
import { cn } from '../lib/utils';
// Alias mince: la lecture d'un type inconnu donne toujours un texte français.
export const operationLabels = tableAvecRepli('financeKind');
export const financialKey = () => `finance-${crypto.randomUUID()}`;
export const amountLabel = (amount, currency = 'XOF') => amount == null ? 'À rapprocher' : formatMontant(amount, currency);

/** Solde d'un compte de caisse ou de banque: inconnu tant que l'ouverture n'est pas saisie. */
const soldeCompte = (montant, devise) => montant == null ? 'À compléter' : formatMontant(montant, devise);
const jour = (valeur) => String(valeur || '').slice(0, 10);
const chronologique = (a, b) =>
  String(a.occurred_at || '').localeCompare(String(b.occurred_at || ''))
  || String(a.created_at || '').localeCompare(String(b.created_at || ''))
  || String(a.id || '').localeCompare(String(b.id || ''));

/**
 * Les lignes du relevé, lisibles comme un livre de caisse: chaque écriture
 * avec son sens (+1 entrée / débit, −1 sortie / crédit), l'effet sur le solde
 * et le solde après elle. Le calcul suit le serveur: seules les écritures
 * confirmées, non annulées, comptent; sur un compte, celles d'avant la date
 * d'ouverture non plus.
 */
export function lignesReleve(data, { accountId } = {}) {
  const operations = Array.isArray(data?.operations) ? data.operations : [];
  const annulees = new Set(
    operations.filter((op) => op.kind === 'REVERSAL' && op.status === 'CONFIRMED').map((op) => op.original_id),
  );
  const compte = accountId ? data?.account || {} : null;
  const ouverture = compte ? jour(compte.opening_date) : '';
  const soldeConnu = !compte || (compte.opening_balance !== null && compte.opening_balance !== undefined);
  const soldes = {};
  const lignes = [...operations].sort(chronologique).map((op) => {
    const signe = compte ? signeCaisse(op, accountId) : SIGNES_TIERS[op.kind] || 0;
    let etat = null;
    if (op.kind === 'REVERSAL') etat = 'annulation';
    else if (op.status === 'PENDING') etat = 'attente';
    else if (op.status !== 'CONFIRMED') etat = 'autre';
    else if (annulees.has(op.id)) etat = 'annulee';
    else if (compte && ouverture && jour(op.occurred_at) < ouverture) etat = 'avant';
    const deviseOp = op.currency || compte?.currency || 'XOF';
    const montant = Number(op.amount) || 0;
    const effet = etat === null ? montant * signe : 0;
    if (soldes[deviseOp] === undefined) soldes[deviseOp] = compte ? Number(compte.opening_balance) || 0 : 0;
    soldes[deviseOp] += effet;
    return {
      operation: op,
      signe,
      etat,
      effet,
      devise: deviseOp,
      solde: etat === null && soldeConnu ? soldes[deviseOp] : null,
    };
  });
  return lignes;
}

/** Totaux des écritures qui comptent: entrées (débit) et sorties (crédit). */
export function totauxReleve(lignes) {
  return lignes.reduce(
    (totaux, ligne) => {
      if (ligne.etat !== null) return totaux;
      if (ligne.effet > 0) totaux.entrees += ligne.effet;
      if (ligne.effet < 0) totaux.sorties -= ligne.effet;
      return totaux;
    },
    { entrees: 0, sorties: 0 },
  );
}

const ETATS = {
  attente: 'En attente · sans effet sur le solde',
  annulee: 'Annulée par une écriture liée',
  annulation: 'Annulation',
  avant: 'Avant l’ouverture du compte',
  autre: 'Sans effet sur le solde',
};

/** Qui et quoi: le tiers, le compte d'en face d'un transfert, la référence. */
const quiEtQuoi = (op, data, accountId) => {
  const details = op.details || {};
  const morceaux = [];
  if (op.party_name) morceaux.push(nomLisible(op.party_name));
  if (op.kind === 'TRANSFER' && accountId) {
    const autre = op.account_id === accountId ? op.target_account_id : op.account_id;
    const nom = data?.account_names?.[autre];
    if (nom) morceaux.push(op.account_id === accountId ? `vers ${nom}` : `depuis ${nom}`);
  }
  const reference = texteSur(details.invoice_number || details.reference || details.reason || details.notes, '');
  if (reference) morceaux.push(reference);
  return morceaux.join(' · ');
};

const Montant = ({ valeur, devise, discret, barre }) => (
  <td className={cn('whitespace-nowrap px-2 py-2 text-right tabular-nums', discret && 'text-muted-foreground', barre && 'line-through')}>
    {valeur ? formatMontant(valeur, devise) : ''}
  </td>
);

export default function FinancialStatement({
  partyType,
  partyId,
  accountId,
  refresh = 0,
  onSelect,
  readOnly = false
}) {
  const [data, setData] = useState(null),
    [error, setError] = useState('');
  const load = useCallback(async () => {
    try {
      const response = await api.get('/finance/statement', {
        params: {
          party_type: partyType,
          party_id: partyId,
          account_id: accountId
        }
      });
      setData(response.data.data);
      setError('');
    } catch (e) {
      setError(messageErreur(e, 'Le relevé n’a pas pu être chargé. Réessayez.'));
    }
  }, [partyType, partyId, accountId]);
  useEffect(() => {
    load();
  }, [load, refresh]);

  const chronologie = useMemo(() => (data ? lignesReleve(data, { accountId }) : []), [data, accountId]);
  // Du plus récent au plus ancien: les derniers mouvements d'abord.
  const lignes = useMemo(() => [...chronologie].reverse(), [chronologie]);
  const totaux = useMemo(() => totauxReleve(chronologie), [chronologie]);
  const pagination = usePagination(lignes, {
    cle: 'releve',
    reinitialiser: `${partyType || ''}|${partyId || ''}|${accountId || ''}`,
    taillePrefere: 50,
  });
  const compte = data?.account || null;
  const devise = compte?.currency || Object.keys(data?.balances || {})[0] || 'XOF';
  const enCaisse = Boolean(accountId);
  const [entete1, entete2] = enCaisse ? ['Entrée', 'Sortie'] : ['Débit', 'Crédit'];
  const actions = !readOnly && Boolean(onSelect);
  const soldeFinal = compte ? data.balance : (data?.balances || {})[devise];
  const allocations = (operationId) => (data?.allocations || []).filter((item) => item.operation_id === operationId);
  const disponible = (operationId) => (data?.available_credits || []).find((item) => item.operation_id === operationId && Number(item.amount) > 0);

  return <section className="space-y-3" aria-label="Relevé des opérations">
    <div className="flex flex-wrap items-center gap-x-4 gap-y-1">
      <h3 className="font-semibold">Relevé des opérations</h3>
      {data && (compte ? <p className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
        <span>Solde d’ouverture <strong className="tabular-nums">{soldeCompte(compte.opening_balance, compte.currency)}</strong>{compte.opening_date ? ` au ${formatDate(compte.opening_date)}` : ''}</span>
        <span>Mouvements <strong className="tabular-nums">{formatMontant(data.known_movements, compte.currency)}</strong></span>
        <span>Solde : <strong className="tabular-nums">{soldeCompte(data.balance, compte.currency)}</strong></span>
      </p> : Object.entries(data.balances || {}).map(([currency, amount]) => <p key={currency} className="flex items-center gap-1 text-sm">Solde : <strong className="tabular-nums">{amountLabel(amount, currency)}</strong>{partyType === 'CUSTOMER' && Number(amount) < 0 ? ' · crédit disponible' : ''}{partyType === 'DRIVER' && <Aide titre="Solde du chauffeur">Argent confié moins frais justifiés et retours.</Aide>}</p>))}
      {data?.needs_reconciliation && <span className="inline-flex items-center gap-1 rounded-full border border-flame/30 bg-flame/10 px-2 py-0.5 text-xs text-flame-ink">Historique à rapprocher<Aide titre="Historique à rapprocher">Certains encaissements n’ont pas de compte de trésorerie identifié.</Aide></span>}
    </div>
    {error && <p role="alert" className="text-destructive">{error}</p>}
    {!data && !error && <LoadingState shape="table" rows={4} columns={5} label="Chargement du relevé" />}
    {data && <>
      {data.checks?.map(check => <p className="text-sm" key={check.id}>Contrôle du {formatDateHeure(check.occurred_at)} : déclaré {amountLabel(check.declared_balance, data.account.currency)} · écart {amountLabel(check.difference, data.account.currency)} · {texteSur(check.reason, 'sans motif')}</p>)}
      {data.documents?.length>0 && <details className="depliant border rounded-lg p-3"><summary className="cursor-pointer">Factures</summary>{data.documents.map(doc=><p className="text-sm py-2" key={doc.id}>{texteSur(doc.reference, 'Sans référence')} · reste dû {amountLabel(doc.remaining,doc.currency)} · {doc.status==='CANCELLED'?'Annulée':doc.status==='PAID'?'Soldée':'Ouverte'}</p>)}</details>}
      {lignes.length > 0 && <div className="overflow-x-auto rounded-lg border">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground">
            <tr className="border-b text-left">
              <th className="px-2 py-2 font-semibold">Date</th>
              <th className="px-2 py-2 font-semibold">Opération</th>
              <th className="px-2 py-2 text-right font-semibold">{entete1}</th>
              <th className="px-2 py-2 text-right font-semibold">{entete2}</th>
              <th className="px-2 py-2 text-right font-semibold">Solde</th>
              {actions && <th className="px-2 py-2"><span className="sr-only">Action</span></th>}
            </tr>
          </thead>
          <tbody>
            {pagination.lignes.map(({ operation, signe, etat, devise: deviseLigne, solde }) => {
              const sansEffet = etat !== null;
              const montant = Number(operation.amount);
              const affecte = allocations(operation.id);
              const credit = disponible(operation.id);
              const qui = quiEtQuoi(operation, data, accountId);
              const complement = [
                ...affecte.map((item) => `Affecté ${amountLabel(item.effective_amount ?? item.amount, deviseLigne)}${Number(item.released_amount) > 0 ? ` · libéré ${amountLabel(item.released_amount, deviseLigne)}` : ''}`),
                credit ? `Disponible ${amountLabel(credit.amount, credit.currency)}` : null,
                operation.details?.needs_reconciliation ? 'Trésorerie à rapprocher' : null,
              ].filter(Boolean);
              return <tr key={operation.id} className={cn('border-b last:border-0 align-top', sansEffet && 'text-muted-foreground')}>
                <td className="whitespace-nowrap px-2 py-2 tabular-nums">{formatDate(operation.occurred_at)}</td>
                <td className="min-w-[14rem] px-2 py-2">
                  <span className="font-medium text-foreground">{libelle('financeKind', operation.kind)}</span>
                  {qui && <span className="text-muted-foreground"> · {qui}</span>}
                  {etat && <span className={cn('block text-xs', etat === 'attente' && 'text-flame-ink')}>{ETATS[etat]}</span>}
                  {complement.length > 0 && <span className="block text-xs text-muted-foreground">{complement.join(' · ')}</span>}
                </td>
                <Montant valeur={signe > 0 ? montant : null} devise={deviseLigne} discret={sansEffet} barre={etat === 'annulee'} />
                <Montant valeur={signe < 0 ? montant : null} devise={deviseLigne} discret={sansEffet} barre={etat === 'annulee'} />
                <td className="whitespace-nowrap px-2 py-2 text-right tabular-nums">{solde === null ? '' : formatMontant(solde, deviseLigne)}</td>
                {actions && <td className="px-2 py-1.5 text-right"><Button size="sm" variant="outline" className="h-7 px-2 text-xs" onClick={() => onSelect(operation)}>Traiter</Button></td>}
              </tr>;
            })}
          </tbody>
          <tfoot className="border-t bg-muted/50 font-semibold">
            <tr>
              <td className="px-2 py-2" colSpan={2}>Total ({lignes.length} opération{lignes.length > 1 ? 's' : ''})</td>
              <td className="whitespace-nowrap px-2 py-2 text-right tabular-nums">{formatMontant(totaux.entrees, devise)}</td>
              <td className="whitespace-nowrap px-2 py-2 text-right tabular-nums">{formatMontant(totaux.sorties, devise)}</td>
              <td className="whitespace-nowrap px-2 py-2 text-right tabular-nums">{soldeFinal == null ? (compte ? 'À compléter' : 'À rapprocher') : formatMontant(soldeFinal, devise)}</td>
              {actions && <td />}
            </tr>
          </tfoot>
        </table>
      </div>}
      <BarrePagination pagination={pagination} nom="opérations" />
      {!lignes.length && <p className="flex items-center gap-1 text-muted-foreground text-sm">Aucune opération dans le journal.<Aide titre="Journal vide">Les données historiques se reprennent après examen dans « Argent ».</Aide></p>}
    </>}
  </section>;
}
